import { Add, Delete, Edit } from '@mui/icons-material';
import { Box, Button, FormHelperText, Grid, IconButton, InputAdornment, OutlinedInput, Stack, Switch, TextField, Typography } from '@mui/material';
import { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';
import CommonDialog from '../../../common/CommonDialog';
import CheckBoxV4 from '../../../components/CheckBoxV4';
import { MuiTextFieldWithFocus } from '../../../config/MaterialUIConstants';
import { focusTooltipInEnd } from '../../../utility/helpers/commonFunction';
import { formatTaxPercent, normalizeShopTaxesData, taxPercentInputRegex } from '../../../utility/helpers/taxCalculation';
import { useTaxDecimalPrecision } from '../../../utility/hooks/useTaxDecimalPrecision';
import SalesTaxModal from './SalesTaxModal';

const SCROLLBAR_STYLES = {
    width: '100%',
    overflowX: 'auto',
    overflowY: 'hidden',

    WebkitOverflowScrolling: 'touch',

    '&::-webkit-scrollbar': {
        height: '8px',
    },

    '&::-webkit-scrollbar-thumb': {
        background: '#BDBDBD',
        borderRadius: '20px',
    },

    '&::-webkit-scrollbar-track': {
        background: '#F5F5F5',
    },

    scrollbarWidth: 'thin',
};
const SALES_TAX_TABLE_STYLES = {
    width: '100%',
    p: '12px',
    border: '1px solid rgba(0, 0, 0, 0.10)',
    borderRadius: '8px',
    boxShadow: '0 2px 3px 0 rgba(0, 0, 0, 0.06)',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    justifyContent: 'center',
    gap: '8px',
    backgroundColor: '#FFF',
    overflowX: 'auto',
};

// Column widths (fixed, in px) for every column except "Sales Tax", which takes the
// remaining space. Kept in one place so the grid template and the per-cell padding
// below always stay in sync.
const RATE_COL_WIDTH = 110; // Invoice % / Estimate %
const CHECKBOX_COL_WIDTH = 90; // Parts / Labour / Fees
const FIXED_PRICE_COL_WIDTH = 100; // Fixed Price (its own width, separate from the other checkbox columns)
const NAME_COL_MIN_WIDTH = 120; // Sales Tax (no max width - grows to fill space)
const CELL_GAP = 20;

const TABLE_GRID_STYLES = {
    display: 'grid',
    gridTemplateColumns: `
        minmax(${NAME_COL_MIN_WIDTH}px, 1fr)
        ${RATE_COL_WIDTH}px
        ${CHECKBOX_COL_WIDTH}px
        ${CHECKBOX_COL_WIDTH}px
        ${FIXED_PRICE_COL_WIDTH}px
        ${CHECKBOX_COL_WIDTH}px
    `,
    columnGap: `${CELL_GAP}px`,
    alignItems: 'center',
    width: '100%',
};

// Total width the grid needs before it should start horizontally scrolling
// (used as the minWidth on the scroll container below).
const TABLE_MIN_WIDTH = NAME_COL_MIN_WIDTH + RATE_COL_WIDTH + CHECKBOX_COL_WIDTH * 3 + FIXED_PRICE_COL_WIDTH + CELL_GAP * 5;

const TABLE_HEADER_STYLES = {
    ...TABLE_GRID_STYLES,
    width: '100%',
    minHeight: '38px',
    backgroundColor: '#F8F8F8',
    px: 2,
    fontSize: '14px',
    fontWeight: 600,
    color: '#1F1F1F',
};

const TABLE_ROW_STYLES = {
    ...TABLE_GRID_STYLES,
    width: '100%',
    minHeight: '60px',
    px: 2,
    py: 0.8,
};

// Header cell for the Tax Rate column - fixed width + 10px side padding.
const HEADER_RATE_CELL_STYLES = {
    width: `${RATE_COL_WIDTH}px`,
    maxWidth: `${RATE_COL_WIDTH}px`,
    px: '10px',
    boxSizing: 'border-box',
};

// Header cell for the Parts / Labour / Fees columns - fixed width, label centered.
const HEADER_CHECKBOX_CELL_STYLES = {
    width: `${CHECKBOX_COL_WIDTH}px`,
    maxWidth: `${CHECKBOX_COL_WIDTH}px`,
    display: 'flex',
    justifyContent: 'center',
    boxSizing: 'border-box',
};

// Header cell for the Fixed Price column - fixed width, label centered.
const HEADER_FIXED_PRICE_CELL_STYLES = {
    width: `${FIXED_PRICE_COL_WIDTH}px`,
    maxWidth: `${FIXED_PRICE_COL_WIDTH}px`,
    display: 'flex',
    justifyContent: 'center',
    boxSizing: 'border-box',
};

// Body cell for the Tax Rate input - fixed width + 10px side padding.
const RATE_CELL_STYLES = {
    width: `${RATE_COL_WIDTH}px`,
    maxWidth: `${RATE_COL_WIDTH}px`,
    px: '10px',
    boxSizing: 'border-box',

    '& .MuiOutlinedInput-root': {
        width: '100%',
        height: '40px',
        borderRadius: '8px',
        boxShadow: '0 2px 3px 0 rgba(0, 0, 0, 0.06)',
        paddingRight: '12px!important',
    },

    '& .MuiOutlinedInput-input': {
        padding: '10px 0 10px 12px',
        fontSize: '14px',
    },
};

// Body cell for the Parts / Labour / Fees checkboxes - fixed width + 10px side padding.
// Body cell for the Parts / Labour / Fees checkboxes - fixed width, checkbox centered.
const CHECKBOX_CELL_STYLES = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: '36px',
    width: `${CHECKBOX_COL_WIDTH}px`,
    maxWidth: `${CHECKBOX_COL_WIDTH}px`,
    boxSizing: 'border-box',
};

// Body cell for the Fixed Price checkbox - fixed width, checkbox centered.
const FIXED_PRICE_CELL_STYLES = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: '36px',
    width: `${FIXED_PRICE_COL_WIDTH}px`,
    maxWidth: `${FIXED_PRICE_COL_WIDTH}px`,
    boxSizing: 'border-box',
};

const mapShopTaxesToLocalState = (shopTaxesData, precision) => {
    const doc = normalizeShopTaxesData(shopTaxesData) || {};
    return {
        // Round rates to the shop's tax precision on load (e.g. an existing 3-decimal value
        // shows as 2 decimals when precision is 2).
        // One rate now drives all three documents. Existing records carry separate rates, so seed
        // from the invoice rate and fall back to whichever one the record does have.
        taxRules: (doc.taxRules || []).map((rule) => ({
            ...rule,
            taxRate: formatTaxPercent(rule.invoiceRate ?? rule.estimateRate ?? rule.workOrderAuthorizedRate ?? 0, precision),
        })),
        singleTaxLineOnPdf: doc.singleTaxLineOnPdf ?? false,
        combinedTaxLabel: doc.combinedTaxLabel ?? '',
    };
};

const buildUpdatePayload = (localState) => ({
    // The UI edits a single rate; the API still keeps one per document type, so the same value goes
    // into all three — invoice, estimate and the work order's own key.
    taxRules: localState.taxRules.map(({ taxName, taxCode, taxRate, appliesToParts, appliesToLabour, appliesToServicePackage, appliesToFees, isDefault }) => ({
        taxName,
        taxCode,
        invoiceRate: parseFloat(taxRate),
        estimateRate: parseFloat(taxRate),
        workOrderAuthorizedRate: parseFloat(taxRate),
        appliesToParts,
        appliesToLabour,
        appliesToServicePackage,
        appliesToFees,
        isDefault: isDefault ?? false,
    })),
    singleTaxLineOnPdf: localState.singleTaxLineOnPdf,
    combinedTaxLabel: localState.combinedTaxLabel,
});

const sanitizeRateInput = (inputValue, fieldLabel, precision) => {
    let numericValue = inputValue.replace(/[^\d.]/g, '');

    if (numericValue.length > 1 && numericValue.startsWith('0') && /[1-9a-zA-Z]/.test(numericValue[1])) {
        numericValue = numericValue.slice(1);
    }

    if (numericValue === '' || taxPercentInputRegex(precision).test(numericValue)) {
        if (Number(numericValue) > 100) {
            toast.error(`${fieldLabel} cannot exceed 100`);
            return null;
        }
        return numericValue;
    }

    return null;
};

const SalesTax = ({ shopDetails, shopTaxesData, updateShopTaxesMutation }) => {
    const [taxRules, setTaxRules] = useState([]);
    const [singleTaxLineOnPdf, setSingleTaxLineOnPdf] = useState(false);
    const [combinedTaxLabel, setCombinedTaxLabel] = useState('');
    const [rateErrors, setRateErrors] = useState({});
    const [combinedLabelError, setCombinedLabelError] = useState(false);
    const [isTaxModalOpen, setIsTaxModalOpen] = useState(false);
    const [updateIndex, setUpdateIndex] = useState(-1);
    const [deleteIndex, setDeleteIndex] = useState(-1);
    const precision = useTaxDecimalPrecision();

    const countryName = shopDetails?.country?.country || shopDetails?.country || '';

    const isUSShop = countryName.trim().toLowerCase() === 'united states';

    const resetLocalState = useCallback(
        (data) => {
            const localState = mapShopTaxesToLocalState(data, precision);
            setTaxRules(localState.taxRules);
            setSingleTaxLineOnPdf(localState.singleTaxLineOnPdf);
            setCombinedTaxLabel(localState.combinedTaxLabel);
            setRateErrors({});
            setCombinedLabelError(false);
        },
        [precision]
    );

    useEffect(() => {
        if (shopTaxesData) {
            resetLocalState(shopTaxesData);
        }
    }, [shopTaxesData, resetLocalState]);

    const handleRateChange = (index, field, inputValue, fieldLabel) => {
        const numericValue = sanitizeRateInput(inputValue, fieldLabel, precision);
        if (numericValue === null) return;

        setTaxRules((prev) => {
            const updated = [...prev];
            updated[index] = { ...updated[index], [field]: numericValue };
            return updated;
        });

        setRateErrors((prev) => ({
            ...prev,
            [`${index}-${field}`]: false,
        }));
    };

    const handleCheckboxChange = (index, field, checked) => {
        setTaxRules((prev) => {
            const updated = [...prev];
            updated[index] = { ...updated[index], [field]: checked };
            return updated;
        });
    };

    const handleDeleteRule = (index) => {
        setTaxRules((prev) => prev.filter((_, i) => i !== index));
    };

    const openDeleteDialog = (index) => {
        setDeleteIndex(index);
    };

    const closeDeleteDialog = () => {
        setDeleteIndex(-1);
    };

    const confirmDeleteRule = () => {
        if (deleteIndex > -1) {
            handleDeleteRule(deleteIndex);
        }
        closeDeleteDialog();
    };

    const openEditModal = (index) => {
        setUpdateIndex(index);
        setIsTaxModalOpen(true);
    };

    const openAddModal = () => {
        setUpdateIndex(-1);
        setIsTaxModalOpen(true);
    };

    const validateBeforeSave = () => {
        const newRateErrors = {};
        let isValid = true;

        taxRules.forEach((rule, index) => {
            const rateNumeric = parseFloat(rule.taxRate);

            if (!rule.taxRate || isNaN(rateNumeric) || rateNumeric <= 0) {
                newRateErrors[`${index}-taxRate`] = true;
                isValid = false;
            }

            if (!rule.appliesToParts && !rule.appliesToLabour && !rule.appliesToServicePackage && !rule.appliesToFees) {
                toast.error(`Please select at least one apply option for ${rule.taxName || 'tax rule'}`);
                isValid = false;
            }
        });

        if (singleTaxLineOnPdf && !combinedTaxLabel.trim()) {
            setCombinedLabelError(true);
            isValid = false;
        } else {
            setCombinedLabelError(false);
        }

        setRateErrors(newRateErrors);
        return isValid;
    };

    // True when the current form differs from what was loaded. Built from the same payload shape
    // both sides, so rate formatting (string vs number), added/removed rules, renamed taxes and
    // every appliesTo/isDefault checkbox are all compared consistently.
    const hasUnsavedChanges = () => {
        const original = JSON.stringify(buildUpdatePayload(mapShopTaxesToLocalState(shopTaxesData, precision)));
        const current = JSON.stringify(buildUpdatePayload({ taxRules, singleTaxLineOnPdf, combinedTaxLabel }));
        return original !== current;
    };

    const handleSave = async () => {
        if (!shopTaxesData?._id) return;

        if (!hasUnsavedChanges()) {
            toast.error('No changes to update.');
            return;
        }

        if (!validateBeforeSave()) return;

        await updateShopTaxesMutation.mutateAsync({
            id: shopTaxesData._id,
            payload: buildUpdatePayload({ taxRules, singleTaxLineOnPdf, combinedTaxLabel }),
        });
    };

    const handleCancel = () => {
        if (shopTaxesData) {
            resetLocalState(shopTaxesData);
        }
    };

    return (
        <Box>
            <Grid
                container
                sx={{ boxShadow: 3, bgcolor: 'white' }}
                justifyContent="space-between"
                padding="24px"
                borderTop="none"
                borderRadius="10px"
                marginTop="32px"
            >
                <Grid item xs={12} sm={4} md={12} xl={4}>
                    <Typography variant="h5" sx={{ fontWeight: 400, fontSize: '24px' }}>
                        Sales Tax
                    </Typography>

                    <Typography
                        variant="body2"
                        sx={{
                            mt: '12px',
                            fontSize: '16px',
                            color: '#1F1F1F',
                            lineHeight: '24px',
                            maxWidth: 360,
                        }}
                    >
                        Setup your sales tax rules to ensure compliance with your local jurisdiction&apos;s requirements.
                    </Typography>

                    <Typography
                        variant="body2"
                        sx={{
                            mt: '12px',
                            fontSize: '16px',
                            color: '#1F1F1F',
                            lineHeight: '24px',
                            maxWidth: 360,
                        }}
                    >
                        These settings will be applied to all new invoices, estimates and work orders.
                    </Typography>
                </Grid>

                <Grid item xs={12} sm={8} md={12} xl={8}>
                    <Box sx={SCROLLBAR_STYLES}>
                        <Box
                            sx={{
                                ...SALES_TAX_TABLE_STYLES,
                                minWidth: `${TABLE_MIN_WIDTH}px`,
                                width: '100%',
                            }}
                        >
                            <Box sx={TABLE_HEADER_STYLES}>
                                <Box sx={{ minWidth: `${NAME_COL_MIN_WIDTH}px`, width: '100%' }}>Sales Tax</Box>

                                <Box sx={HEADER_RATE_CELL_STYLES}>Tax Rate</Box>

                                <Box sx={HEADER_CHECKBOX_CELL_STYLES}>Parts</Box>

                                <Box sx={HEADER_CHECKBOX_CELL_STYLES}>Labour</Box>

                                <Box sx={HEADER_FIXED_PRICE_CELL_STYLES}>Fixed Price</Box>

                                <Box sx={HEADER_CHECKBOX_CELL_STYLES}>Fees</Box>
                            </Box>

                            {taxRules.map((rule, index) => (
                                <Box sx={TABLE_ROW_STYLES} key={rule._id || `${rule.taxName}-${index}`}>
                                    <Box
                                        sx={{
                                            display: 'flex',
                                            flexDirection: 'column',
                                            gap: '2px',
                                            minWidth: `${NAME_COL_MIN_WIDTH}px`,
                                            width: '100%',
                                        }}
                                    >
                                        <Box
                                            sx={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: '8px',
                                                minWidth: 0,
                                                width: '100%',
                                            }}
                                        >
                                            <Typography
                                                variant="body1"
                                                className="label-500"
                                                sx={{
                                                    flexShrink: 1,
                                                    minWidth: 0,
                                                    maxWidth: '100%',
                                                    fontSize: '16px',
                                                    color: '#1F1F1F',
                                                    overflow: 'hidden',
                                                    textOverflow: 'ellipsis',
                                                    whiteSpace: 'nowrap',
                                                }}
                                            >
                                                {rule.taxName}
                                            </Typography>

                                            <Box
                                                sx={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'flex-start',
                                                    gap: '4px',
                                                    flexShrink: 0,
                                                }}
                                            >
                                                {isUSShop && (
                                                    <IconButton
                                                        size="small"
                                                        aria-label="Edit sales tax"
                                                        onClick={() => openEditModal(index)}
                                                        sx={{ p: '4px', width: 24, height: 24, alignItems: 'center' }}
                                                    >
                                                        <Edit sx={{ fontSize: 20, color: '#646466' }} />
                                                    </IconButton>
                                                )}

                                                {isUSShop && index > 0 && (
                                                    <IconButton
                                                        size="small"
                                                        aria-label="Delete sales tax"
                                                        onClick={() => openDeleteDialog(index)}
                                                        sx={{ p: '4px', width: 24, height: 24, flexShrink: 0 }}
                                                    >
                                                        <Delete sx={{ fontSize: 18, color: '#D32F2F' }} />
                                                    </IconButton>
                                                )}
                                            </Box>
                                        </Box>

                                        <Typography
                                            sx={{
                                                fontSize: 14,
                                                fontWeight: 400,
                                                color: '#666666',
                                                lineHeight: '16.8px',
                                                letterSpacing: ' 0.15px',
                                                textTransform: 'uppercase',
                                            }}
                                        >
                                            {rule.taxCode}
                                        </Typography>
                                    </Box>

                                    <Stack gap={0.75} sx={RATE_CELL_STYLES}>
                                        <OutlinedInput
                                            value={rule.taxRate?.toString() ?? ''}
                                            error={!!rateErrors[`${index}-taxRate`]}
                                            onFocus={focusTooltipInEnd}
                                            endAdornment={<InputAdornment position="end">%</InputAdornment>}
                                            onChange={(event) => handleRateChange(index, 'taxRate', event.target.value, 'Sales tax')}
                                            fullWidth
                                        />
                                        {rateErrors[`${index}-taxRate`] && (
                                            <FormHelperText sx={{ marginTop: '0' }} error>
                                                Sales tax should be greater than 0
                                            </FormHelperText>
                                        )}
                                    </Stack>

                                    <Box sx={CHECKBOX_CELL_STYLES}>
                                        <CheckBoxV4
                                            checked={!!rule.appliesToParts}
                                            onChange={(event) => handleCheckboxChange(index, 'appliesToParts', event.target.checked)}
                                            sx={{ p: 0 }}
                                        />
                                    </Box>

                                    <Box sx={CHECKBOX_CELL_STYLES}>
                                        <CheckBoxV4
                                            checked={!!rule.appliesToLabour}
                                            onChange={(event) => handleCheckboxChange(index, 'appliesToLabour', event.target.checked)}
                                            sx={{ p: 0 }}
                                        />
                                    </Box>

                                    <Box sx={FIXED_PRICE_CELL_STYLES}>
                                        <CheckBoxV4
                                            checked={!!rule.appliesToServicePackage}
                                            onChange={(event) => handleCheckboxChange(index, 'appliesToServicePackage', event.target.checked)}
                                            sx={{ p: 0 }}
                                        />
                                    </Box>

                                    <Box sx={CHECKBOX_CELL_STYLES}>
                                        <CheckBoxV4
                                            checked={!!rule.appliesToFees}
                                            onChange={(event) => handleCheckboxChange(index, 'appliesToFees', event.target.checked)}
                                            sx={{ p: 0 }}
                                        />
                                    </Box>
                                </Box>
                            ))}
                        </Box>
                    </Box>

                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'flex-start',
                            justifyContent: 'space-between',
                            flexWrap: 'wrap',
                            gap: 2,
                            mt: '13px',
                        }}
                    >
                        {isUSShop && (
                            <Button
                                variant="outlined"
                                startIcon={<Add />}
                                onClick={openAddModal}
                                sx={{
                                    height: '38px',
                                    padding: '6px 12px',
                                    borderRadius: '6px',
                                    border: '1px solid #006CDD',
                                    background: '#FFF',
                                    color: '#006CDD',
                                    fontSize: '15px',
                                    fontWeight: 500,
                                    lineHeight: '26px',
                                    textTransform: 'uppercase',
                                    '&:hover': {
                                        border: '1px solid #006CDD',
                                        background: '#FFF',
                                    },
                                }}
                            >
                                Additional Tax
                            </Button>
                        )}
                        {isUSShop && (
                            <Box sx={{ ml: 'auto', display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 1.5 }}>
                                <Stack direction="row" alignItems="center" spacing={1.5}>
                                    <Typography variant="body1" fontSize="16px" fontWeight={400} color="rgba(0, 0, 0, 0.87)">
                                        Single Tax Line on PDF
                                    </Typography>
                                    <Switch checked={singleTaxLineOnPdf} onChange={(event) => setSingleTaxLineOnPdf(event.target.checked)} />
                                </Stack>

                                {singleTaxLineOnPdf && (
                                    <Box sx={{ width: '100%', maxWidth: 320 }}>
                                        <TextField
                                            fullWidth
                                            variant="outlined"
                                            placeholder="Combined Tax Label"
                                            value={combinedTaxLabel}
                                            onChange={(event) => {
                                                setCombinedTaxLabel(event.target.value);
                                                setCombinedLabelError(false);
                                            }}
                                            error={combinedLabelError}
                                            helperText={combinedLabelError ? 'Combined tax label is required' : ''}
                                            sx={MuiTextFieldWithFocus}
                                        />
                                    </Box>
                                )}
                            </Box>
                        )}
                    </Box>

                    <Grid item container justifyContent="flex-end" spacing={2} sx={{ marginTop: '16px' }}>
                        <Grid item>
                            <Stack spacing={2} direction="row">
                                <Button className="btn-text-size-manage" variant="outlined" color="primary" onClick={handleCancel}>
                                    Cancel
                                </Button>

                                <Button
                                    className="btn-text-size-manage"
                                    variant="contained"
                                    color="primary"
                                    onClick={handleSave}
                                    disabled={updateShopTaxesMutation.isPending}
                                >
                                    Save
                                </Button>
                            </Stack>
                        </Grid>
                    </Grid>
                </Grid>
            </Grid>

            <SalesTaxModal
                open={isTaxModalOpen}
                onClose={setIsTaxModalOpen}
                onSave={setTaxRules}
                updateIndex={updateIndex}
                setUpdateIndex={setUpdateIndex}
                taxRules={taxRules}
            />

            <CommonDialog
                open={deleteIndex > -1}
                onClose={closeDeleteDialog}
                onCancel={closeDeleteDialog}
                onSave={confirmDeleteRule}
                dialogClaassName="commonDialog"
                dialogTitle="⚠️ Delete Tax?"
                dialogSubtitle="Are you sure you want to delete this tax type?"
                dialogSubtitle2="This action cannot be undone."
                cancelButtonText="Cancel"
                saveButtonText="Delete"
            />
        </Box>
    );
};

export default SalesTax;
